import { Errors } from '/imports/validators';

// Ceilings on what a client may send. They sit far above anything real — across the 20,000 most recent
// method calls the longest argument string was under 2,000 characters — so they exist to stop abuse and
// runaway bugs, never a user with a lot to say.

/** Any single string inside a method argument. */
export const maxMethodStringLength = 1000000;
/** Every string inside one method argument, summed. Bounds a huge array of individually small strings. */
export const maxMethodPayloadLength = 8000000;
/** Any string field on a stored document, unless it is named in LONG_FIELDS. */
export const maxFieldLength = 10000;
/** Fields people write prose into. */
export const maxLongFieldLength = 200000;

// Guards against a pathological shape costing more to inspect than to store.
const MAX_DEPTH = 12;
const MAX_NODES = 50000;

const LONG_FIELDS = new Set([
  'notes', 'description', 'content', 'message', 'body', 'text', 'comment', 'summary', 'instructions',
  'propertyLegalDescription', 'easementsMetesAndBounds',
]);

/**
 * The character ceiling for one stored field. A model's own declaration wins; otherwise prose fields get
 * the long allowance and everything else, including every field nobody has thought about, gets the default.
 * @param field - the field name
 * @param limits - the model's declared per-field ceilings, if it has any
 * @returns the maximum string length allowed in that field
 */
export const fieldLimit = (field: string, limits?: FieldLimits): number => {
  const declared = limits?.[field];
  if (typeof declared === 'number') return declared;
  return LONG_FIELDS.has(field) ? maxLongFieldLength : maxFieldLength;
};

export type FieldLimits = { [field: string]: number | undefined };

/**
 * Walks a value for the first string that breaks a ceiling.
 * @param value - the value to inspect
 * @param maxString - the longest a single string may be
 * @param maxTotal - the longest every string in the value may be, summed
 * @returns a description of the first violation, or undefined when the value is within its ceilings
 */
export const findOverLongString = (value: unknown, maxString: number, maxTotal = Number.MAX_SAFE_INTEGER): string | undefined => {
  let total = 0;
  let nodes = 0;
  const walk = (v: unknown, path: string, depth: number): string | undefined => {
    if (v === null || v === undefined) return undefined;
    if (depth > MAX_DEPTH) return `${path || 'argument'} is nested deeper than ${MAX_DEPTH}`;
    if (++nodes > MAX_NODES) return `argument holds more than ${MAX_NODES} values`;
    if (typeof v === 'string') {
      if (v.length > maxString) return `${path || 'argument'} is ${v.length} characters, over the ${maxString} limit`;
      total += v.length;
      if (total > maxTotal) return `argument holds more than ${maxTotal} characters of text`;
      return undefined;
    }
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) {
        const hit = walk(v[i], `${path}[${i}]`, depth + 1);
        if (hit) return hit;
      }
      return undefined;
    }
    if (typeof v === 'object') {
      if (v instanceof Date || v instanceof RegExp || Buffer.isBuffer?.(v)) return undefined;
      for (const key of Object.keys(v as Record<string, unknown>)) {
        const hit = walk((v as Record<string, unknown>)[key], path ? `${path}.${key}` : key, depth + 1);
        if (hit) return hit;
      }
    }
    return undefined;
  };
  return walk(value, '', 0);
};

/**
 * Field-level length errors for a document payload, keyed by the top-level field so a form can show them
 * against the input the user typed into. A string nested inside a field is held to that field's ceiling.
 * @param doc - the document or update payload
 * @returns an errors map in the shape validationGuard expects, empty when everything fits
 */
export const findOverLongFields = (
  doc: Record<string, unknown> | undefined,
  limits?: FieldLimits
): { [field: string]: string[] } => {
  const errors: { [field: string]: string[] } = {};
  for (const [field, value] of Object.entries(doc || {})) {
    if (findOverLongString(value, fieldLimit(field, limits))) errors[field] = [Errors.tooLong];
  }
  return errors;
};
